import {
  MousePointerClick,
  Globe,
  Smartphone,
  Braces,
  RotateCcw,
  ClipboardList,
  UserCheck,
  Compass,
  Zap,
  ShieldCheck,
  Layers,
  Users,
  Rocket,
  Building2,
  Package,
  Code2,
  TrendingUp,
} from "lucide-react";

export const COMPANY = {
  name: "Avirova Technologies",
  tagline: "Your QA team, without the overhead.",
  email: "hello@avirova.tech",
  linkedin: "[LinkedIn URL]",
};

export const VALUE_CARDS = [
  {
    icon: Zap,
    title: "Faster Releases",
    text: "Identify critical issues before they reach production.",
  },
  {
    icon: ShieldCheck,
    title: "Better Product Quality",
    text: "Systematic testing across features, workflows, devices and APIs.",
  },
  {
    icon: Layers,
    title: "Flexible QA Capacity",
    text: "Scale testing support up or down according to your release cycle.",
  },
  {
    icon: Users,
    title: "Reduced Hiring Overhead",
    text: "Get experienced QA support without immediately building a large internal team.",
  },
];

export const SERVICES = [
  {
    slug: "manual-testing",
    icon: MousePointerClick,
    title: "Manual Testing",
    blurb:
      "Functional, exploratory, regression and usability testing for web and software applications.",
    span: true,
  },
  {
    slug: "web-application-testing",
    icon: Globe,
    title: "Web Application Testing",
    blurb: "Cross-browser, functional, UI, workflow and compatibility testing.",
    span: false,
  },
  {
    slug: "mobile-application-testing",
    icon: Smartphone,
    title: "Mobile Application Testing",
    blurb:
      "Testing across Android and iOS devices, screen sizes and operating-system versions.",
    span: false,
  },
  {
    slug: "api-testing",
    icon: Braces,
    title: "API Testing",
    blurb:
      "API validation using tools such as Postman, including functional, negative and response validation testing.",
    span: true,
  },
  {
    slug: "regression-testing",
    icon: RotateCcw,
    title: "Regression Testing",
    blurb: "Ensure new releases don't break existing functionality.",
    span: false,
  },
  {
    slug: "test-case-design",
    icon: ClipboardList,
    title: "Test Case & Scenario Design",
    blurb:
      "Detailed and maintainable test scenarios, test cases and test suites.",
    span: false,
  },
  {
    slug: "uat-support",
    icon: UserCheck,
    title: "UAT Support",
    blurb:
      "Support business teams during user acceptance testing and release validation.",
    span: false,
  },
  {
    slug: "qa-consulting",
    icon: Compass,
    title: "QA Consulting",
    blurb:
      "Help startups establish QA processes, testing strategies and release-quality practices.",
    span: false,
  },
];

export const PROCESS_STEPS = [
  {
    num: "01",
    title: "Understand",
    text: "We understand your product, users, workflows and business requirements.",
  },
  {
    num: "02",
    title: "Plan",
    text: "We define the testing scope, scenarios, risks and priorities.",
  },
  {
    num: "03",
    title: "Test",
    text: "We execute functional, regression, API, mobile and exploratory testing as required.",
  },
  {
    num: "04",
    title: "Report & Improve",
    text: "We provide clear defect reports, test summaries and actionable QA insights.",
  },
];

export const MANIFESTO = [
  {
    title: "Experienced QA professionals",
    text: "Your product is tested by people who do this every day, not as a side task.",
  },
  {
    title: "Flexible engagement models",
    text: "One-time, ongoing or dedicated support — matched to your release cycle.",
  },
  {
    title: "Clear defect reporting",
    text: "Reproducible steps, severity, evidence. No ambiguity, no noise.",
  },
  {
    title: "Risk-based testing",
    text: "We prioritise what can hurt your users and your business the most.",
  },
  {
    title: "Fast turnaround",
    text: "QA that keeps pace with your sprints instead of blocking them.",
  },
  {
    title: "Startup-friendly approach",
    text: "Pragmatic processes that fit small teams and tight timelines.",
  },
  {
    title: "Scalable QA support",
    text: "Add coverage when you grow. Pull back when you don't need it.",
  },
  {
    title: "Transparent communication",
    text: "You always know what was tested, what failed, and what it means.",
  },
];

export const CASE_STUDIES = [
  {
    id: "01",
    title: "SaaS CRM Application",
    label: "Sample QA Case Study",
    summary:
      "End-to-end functional and regression coverage for a multi-role CRM platform.",
    scope: [
      "Functional testing",
      "Role & permission testing",
      "Regression testing",
      "API testing",
      "Search & filter testing",
    ],
    challenge:
      "A CRM platform with multiple user roles, lead pipelines and integrations needed structured testing before each release. Defects in role-based access and search behaviour were reaching end users. [Replace with a real client scenario when available.]",
    approach:
      "Mapped critical workflows per role, built a maintainable regression suite, and validated API responses behind key UI flows. Testing was prioritised by business risk: access control, data integrity and revenue-critical paths first.",
    coverage: [
      "User management & onboarding",
      "Role-based access control",
      "Lead management workflows",
      "Search & filtering behaviour",
      "API request/response validation",
      "Full regression suite per release",
    ],
    findings:
      "Critical defects were identified in permission boundaries and search indexing edge cases before release. [Add verified result here]",
    outcome:
      "The release process became predictable, with a repeatable regression suite and clear go/no-go reporting each cycle. [Add verified result here]",
  },
  {
    id: "02",
    title: "E-commerce Web Application",
    label: "Sample QA Case Study",
    summary:
      "Checkout, payment and order-management validation for a high-traffic storefront.",
    scope: [
      "Product search",
      "Cart",
      "Checkout",
      "Order management",
      "Payment workflow",
      "Regression",
    ],
    challenge:
      "An e-commerce application was losing revenue to checkout friction and inconsistent order states across browsers and devices. [Replace with a real client scenario when available.]",
    approach:
      "Built end-to-end purchase-path scenarios across browsers, validated payment workflow states (success, decline, timeout), and stress-tested cart and inventory edge cases such as concurrent stock changes.",
    coverage: [
      "Product search & filtering",
      "Cart behaviour & edge cases",
      "Checkout & address flows",
      "Order lifecycle management",
      "Payment workflow states",
      "Cross-browser regression",
    ],
    findings:
      "Defects surfaced in payment retry handling and order-state transitions that previously reached production. [Add verified result here]",
    outcome:
      "Checkout releases shipped with structured sign-off, and payment-flow regressions were caught in testing rather than by customers. [Add verified result here]",
  },
  {
    id: "03",
    title: "Mobile Application",
    label: "Sample QA Case Study",
    summary:
      "Device, network and UI compatibility testing for a consumer mobile app.",
    scope: [
      "Functional testing",
      "Device compatibility",
      "Network testing",
      "UI testing",
      "Regression",
    ],
    challenge:
      "A mobile app behaved inconsistently across Android and iOS versions, with UI breakages on smaller screens and failures on unstable networks. [Replace with a real client scenario when available.]",
    approach:
      "Defined a device and OS coverage matrix, executed functional and UI testing across screen sizes, and simulated real network conditions — offline, 3G, interruptions and reconnection.",
    coverage: [
      "Core functional flows",
      "Device & OS compatibility matrix",
      "Network interruption & offline states",
      "UI consistency across screen sizes",
      "Per-release regression",
    ],
    findings:
      "Compatibility defects were isolated to specific OS versions and screen densities, giving developers precise reproduction paths. [Add verified result here]",
    outcome:
      "Release confidence improved across devices, with compatibility issues caught in a repeatable matrix instead of app-store reviews. [Add verified result here]",
  },
];

export const AUDIENCES = [
  {
    icon: Rocket,
    title: "Startups",
    text: "Pre-product-market-fit teams that need quality without a QA hire.",
  },
  {
    icon: Building2,
    title: "SaaS companies",
    text: "Subscription products where every regression costs churn.",
  },
  {
    icon: Package,
    title: "Product companies",
    text: "Teams shipping features continuously across web and mobile.",
  },
  {
    icon: Code2,
    title: "Software agencies",
    text: "Delivery teams that need reliable QA capacity for client work.",
  },
  {
    icon: TrendingUp,
    title: "Growing businesses",
    text: "Companies professionalising their release process as they scale.",
  },
];

export const ENGAGEMENT_MODELS = [
  {
    num: "01",
    title: "One-Time QA Testing",
    text: "For specific releases, features or applications. A defined scope, a clear report, done.",
  },
  {
    num: "02",
    title: "Ongoing QA Support",
    text: "For companies requiring continuous testing alongside their development cycle.",
  },
  {
    num: "03",
    title: "Dedicated QA",
    text: "For businesses that need a dedicated testing resource embedded with their team.",
  },
];

export const DETAILED_SERVICES = [
  {
    slug: "manual-testing",
    num: "01",
    title: "Manual Testing",
    includes: [
      "Functional testing of features and workflows",
      "Exploratory testing beyond the scripted path",
      "Usability and UI behaviour review",
      "Regression execution before releases",
    ],
    need: "You ship features regularly but nobody owns quality end-to-end, and defects are found by users instead of your team.",
    receive: [
      "Executed test runs with clear pass/fail status",
      "Defect reports with steps, severity and evidence",
      "A release-readiness summary",
    ],
  },
  {
    slug: "functional-testing",
    num: "02",
    title: "Functional Testing",
    includes: [
      "Requirement and acceptance-criteria validation",
      "Workflow, form, validation and state testing",
      "Positive, negative and edge-case scenarios",
      "Integration point verification",
    ],
    need: "Your product does what it was built to do — most of the time. You need proof that every feature behaves as specified, every time.",
    receive: [
      "Feature-by-feature coverage matrix",
      "Documented deviations from expected behaviour",
      "Traceability between requirements and test results",
    ],
  },
  {
    slug: "regression-testing",
    num: "03",
    title: "Regression Testing",
    includes: [
      "Maintainable regression suite design",
      "Full or targeted regression before each release",
      "Impact-area analysis for faster cycles",
      "Release go/no-go reporting",
    ],
    need: "Every new release carries the risk of breaking something that already worked — and your team finds out too late.",
    receive: [
      "A regression suite that grows with your product",
      "Per-release regression reports",
      "Confidence that existing functionality still works",
    ],
  },
  {
    slug: "web-application-testing",
    num: "04",
    title: "Web Application Testing",
    includes: [
      "Cross-browser testing (Chrome, Firefox, Safari, Edge)",
      "Responsive behaviour across breakpoints",
      "Workflow, UI and compatibility testing",
      "Accessibility and basic performance checks",
    ],
    need: "Your users are on different browsers, screens and devices — and your app doesn't behave the same on all of them.",
    receive: [
      "Browser and device coverage report",
      "UI inconsistency documentation with screenshots",
      "Verified core workflows per environment",
    ],
  },
  {
    slug: "mobile-application-testing",
    num: "05",
    title: "Mobile Application Testing",
    includes: [
      "Android and iOS functional testing",
      "Device, screen-size and OS-version matrix",
      "Network condition and interruption testing",
      "Install, update and permission flows",
    ],
    need: "Mobile users are unforgiving. Crashes, layout breaks and offline failures turn into one-star reviews fast.",
    receive: [
      "Device/OS compatibility matrix results",
      "Defects isolated to specific devices or versions",
      "Network-resilience behaviour report",
    ],
  },
  {
    slug: "api-testing",
    num: "06",
    title: "API Testing",
    includes: [
      "Request/response validation using Postman",
      "Functional, negative and boundary testing",
      "Status code, schema and payload verification",
      "Authentication and error-handling checks",
    ],
    need: "Your UI is only as reliable as the APIs beneath it — and API defects are invisible until something breaks upstream.",
    receive: [
      "Documented API test collections",
      "Validation results per endpoint",
      "Early detection of contract and data issues",
    ],
  },
  {
    slug: "uat-support",
    num: "07",
    title: "UAT Support",
    includes: [
      "UAT planning and scenario preparation",
      "Support for business teams during acceptance",
      "Defect triage and verification of fixes",
      "Release validation sign-off",
    ],
    need: "Business stakeholders are asked to 'test' releases without structure, time or testing experience — and sign-off becomes a bottleneck.",
    receive: [
      "Structured UAT scenarios mapped to business flows",
      "Triaged defect list with priorities",
      "A clear acceptance summary for stakeholders",
    ],
  },
  {
    slug: "test-case-design",
    num: "08",
    title: "Test Case & Test Scenario Creation",
    includes: [
      "Test scenario identification from requirements",
      "Detailed, maintainable test cases",
      "Organised test suites per module",
      "Coverage review against requirements",
    ],
    need: "Testing happens ad hoc, knowledge lives in people's heads, and nothing is repeatable when someone is away.",
    receive: [
      "A structured, reusable test repository",
      "Coverage visibility per feature",
      "Documentation your whole team can execute",
    ],
  },
  {
    slug: "qa-consulting",
    num: "09",
    title: "QA Process Consulting",
    includes: [
      "Review of your current release and testing process",
      "QA strategy and risk-based prioritisation",
      "Defect workflow and reporting standards",
      "Release-quality practices for growing teams",
    ],
    need: "You're a startup or growing team that wants to do QA properly before hiring a full internal team.",
    receive: [
      "A practical QA strategy document",
      "Process recommendations matched to your team size",
      "Templates for reporting and release sign-off",
    ],
  },
  {
    slug: "automation-testing",
    num: "10",
    title: "Automation Testing",
    comingSoon: true,
    includes: [
      "Test automation strategy and framework selection",
      "Automated regression for core workflows",
      "CI pipeline integration guidance",
    ],
    need: "Your regression suite has grown to the point where manual execution slows every release.",
    receive: [
      "This service is currently in development",
      "Join the waitlist via the contact form",
    ],
  },
];

export const MARQUEE_ITEMS = [
  "Functional Testing",
  "Regression",
  "API Validation",
  "Cross-Browser",
  "Mobile QA",
  "Exploratory Testing",
  "UAT Support",
  "Test Case Design",
  "Postman",
  "Playwright",
  "Release Sign-Off",
  "Risk-Based Testing",
];

export const CONTACT_OPTIONS = {
  requirement: [
    "Manual Testing",
    "Regression Testing",
    "API Testing",
    "Mobile Testing",
    "Web Testing",
    "UAT Support",
    "QA Consulting",
    "Other",
  ],
  companySize: ["1–10", "11–50", "51–200", "201–500", "500+"],
  qaSetup: [
    "No dedicated QA",
    "Small in-house QA team",
    "Outsourced QA (need more/change)",
    "Building a QA team",
    "Other",
  ],
};
